import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CreateModelInput,
  CreateModelMutation,
  CreateModelMutationVariables,
  Engagements,
  GetModelQuery,
  GetModelQueryVariables,
  Model,
  ModelStatus,
  ModelType,
} from '../../../../API';
import { ApiService } from '../../../shared/services/api.service';
import { GetModel } from '../../../../graphql/queries';
import { AppService } from '../../../shared/services/app.service';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AT_LEAST_ONE_ALPHABET, NOT_ALLOWED_SPECIAL_CHARS, ONLY_NUMBERS } from '../../../shared/utils/constants';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { CreateModel } from '../../../../graphql/mutations';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-view-model',
  templateUrl: './view-model.component.html',
  styleUrl: './view-model.component.scss',
})
export class ViewModelComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  engagement!: string;

  modelData?: Model;
  isLoading: boolean = false;
  lookalikeForm!: FormGroup;
  MAX_LOOKALIKE_NUMBER: number = 2_000_000;
  WHITESPACE_DISTRIBUTION_IMAGE: string = '';
  imagesForLookalike: string[] = [];

  constructor(
    private apiService: ApiService,
    private appService: AppService,
    private activatedRoute: ActivatedRoute,
    private nzMessageService: NzMessageService,
    private formBuilder: FormBuilder,
    private userManagementService: UserManagementService,
    private router: Router,
  ) {
    this.initializeLookalikeForm();
  }

  ngOnInit() {
    this.appService.activeEngagement$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev.engagement !== curr.engagement),
      )
      .subscribe((engagement: Engagements) => {
        this.engagement = engagement.engagement;
        this.getModelData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.modelData = undefined;
  }

  getModelData() {
    const modelId = this.activatedRoute.snapshot.paramMap.get('modelId') || '';
    this.isLoading = true;
    this.apiService
      .query<GetModelQuery, GetModelQueryVariables>(GetModel, {
        engagementId: this.engagement,
        modelId: modelId,
      })
      .then((response) => {
        const result = response.data?.GetModel;
        if (result) {
          this.modelData = result;
          this.setLookalikeValidator();
          this.WHITESPACE_DISTRIBUTION_IMAGE = this.modelData?.images?.[0] ?? 'Whitespace_Distribution.png';
          this.imagesForLookalike = (this.modelData?.images ?? []).splice(1);
        } else {
          this.nzMessageService.error('Failed to get model data');
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to get the model data due to ${error?.message || error} error`);
      })
      .finally(() => (this.isLoading = false));
  }

  getImageUrl(imageName: string | undefined | null): string {
    if (!imageName) return '';
    if (this.WHITESPACE_DISTRIBUTION_IMAGE === imageName || this.modelData?.modelType === ModelType.BASE) {
      const modelId = this.modelData?.modelType === ModelType.BASE ? this.modelData?.modelId : this.modelData?.parentId;
      return `${this.appService.domainUrl}/images/ml/${this.modelData?.engagement}/${modelId}/${imageName}`;
    }
    return `${this.appService.domainUrl}/images/ml/${this.modelData?.engagement}/${this.modelData?.parentId}/${this.modelData?.modelId}/${imageName}`;
  }

  getImageHeader(imageName: string | undefined | null): string {
    if (!imageName) return '';
    return imageName.split('.png')[0]?.split('_').join(' ');
  }

  private initializeLookalikeForm(): void {
    this.lookalikeForm = this.formBuilder.group({
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
        ],
      ],
      lookalikeNumber: [''],
    });
    this.setLookalikeValidator();
  }

  private setLookalikeValidator(): void {
    if (this.modelData?.maxLookalikeNumber && this.modelData?.maxLookalikeNumber < this.MAX_LOOKALIKE_NUMBER) {
      this.MAX_LOOKALIKE_NUMBER = this.modelData.maxLookalikeNumber;
    }
    this.lookalikeNumberControl.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(this.MAX_LOOKALIKE_NUMBER),
      Validators.pattern(ONLY_NUMBERS),
    ]);
  }

  createLookalike(): void {
    this.isLoading = true;
    const { name, lookalikeNumber } = this.lookalikeForm.value;
    const createModelInput: CreateModelInput = {
      modelId: uuidv4(),
      name: name,
      engagement: this.engagement,
      queryId: this.modelData!.queryId,
      queryName: this.modelData!.name,
      athenaExecutionId: this.modelData!.athenaExecutionId,
      seedAudienceSize: this.modelData?.seedAudienceSize,
      lookalikeNumber: lookalikeNumber,
      createdBy: this.userManagementService.getEmail(),
      orgType: this.userManagementService.getOrgType(),
      status: ModelStatus.IN_PROGRESS,
      modelType: ModelType.LOOKALIKE,
      parentId: this.modelData?.modelId,
    };

    this.apiService
      .query<CreateModelMutation, CreateModelMutationVariables>(CreateModel, {
        input: createModelInput,
      })
      .then((response) => {
        if (response.data?.CreateModel?.success) {
          this.nzMessageService.success('Lookalike model created successfully!');
          this.goToHistory();
        } else {
          this.nzMessageService.error(response.data?.CreateModel?.statusMessage || 'Failed to create lookalike model');
        }
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message ?? 'Failed to create lookalike model');
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  goToHistory(): void {
    this.router.navigate(['/ml/history']);
  }

  get lookalikeNumberControl(): FormControl {
    return this.lookalikeForm.get('lookalikeNumber') as FormControl;
  }
}
